import type { ReactElement, ReactNode } from 'react';
import { Div } from '@fastnorth/kraken/components/layout';
import type { SubEventProgressOverview } from '../types';
import RaceProgressRacesSummary from './RaceProgressRacesSummary';
import RaceProgressPageHeader from './RaceProgressPageHeader';
import RaceProgressParticipantLookupSection from './RaceProgressParticipantLookupSection';
import RaceProgressSectionSeparator from './RaceProgressSectionSeparator';
import SubEventProgressSection from './SubEventProgressSection';
import BackToTop from './BackToTop';

export type RaceProgressPageContentProps = {
  pageTopElement?: HTMLDivElement | null;
  overviewLoading?: boolean;
  raceDetailsFallback?: ReactNode;
  refreshErrorContent?: ReactNode;
  secondsUntilUpdate?: number;
  summarySkeletonRowCount?: number;
  noRacesContent?: ReactNode;
  errorContent?: ReactNode;
  subEvents: SubEventProgressOverview[];
  testId?: string;
  timeZone: string;
  timezoneShort?: string;
};

const RaceProgressPageContent = ({
  pageTopElement,
  overviewLoading = false,
  raceDetailsFallback,
  refreshErrorContent,
  secondsUntilUpdate,
  summarySkeletonRowCount = 0,
  noRacesContent,
  subEvents,
  testId = 'event-progress-v2-page',
  timeZone,
  timezoneShort,
  errorContent,
}: RaceProgressPageContentProps): ReactElement => {
  const participantLookupAwaitingRaces: boolean = overviewLoading || noRacesContent !== undefined;
  const header: ReactElement = (
    <RaceProgressPageHeader secondsUntilUpdate={secondsUntilUpdate} timezoneShort={timezoneShort} />
  );

  if (errorContent) {
    return (
      <Div data-testid={testId}>
        <Div className="px-8">{header}</Div>
        <RaceProgressSectionSeparator />
        <Div className="px-8">{errorContent}</Div>
      </Div>
    );
  }

  const showBackToTop: boolean = !overviewLoading && raceDetailsFallback == null;

  return (
    <Div data-testid={testId}>
      <Div className="px-8">{header}</Div>
      <RaceProgressSectionSeparator />
      {refreshErrorContent ? <Div className="px-8">{refreshErrorContent}</Div> : null}
      <Div className="px-8">
        <RaceProgressRacesSummary
          emptyContent={noRacesContent}
          loadingRowCount={overviewLoading ? summarySkeletonRowCount : 0}
          subEvents={subEvents}
        />
      </Div>
      <RaceProgressSectionSeparator />
      <Div className="px-8">
        <RaceProgressParticipantLookupSection awaitingRaces={participantLookupAwaitingRaces} />
      </Div>
      <RaceProgressSectionSeparator />
      <Div className="min-w-0 max-w-full px-8" data-testid="race-progress-race-details">
        {raceDetailsFallback ??
          subEvents.map((subEvent, index) => (
            <Div key={subEvent.subEventId}>
              <SubEventProgressSection subEvent={subEvent} timeZone={timeZone} />
              {index < subEvents.length - 1 ? <RaceProgressSectionSeparator /> : null}
            </Div>
          ))}
      </Div>
      {showBackToTop ? <BackToTop scrollToElement={pageTopElement ?? null} /> : null}
    </Div>
  );
};

export default RaceProgressPageContent;
